import Image from 'next/image';
import { useRef, useState } from 'react';

type Props = {
  alt: string;
  className?: string;
  posterUrl: string;
  sizes: string;
  videoUrl?: string | null;
};

export default function NewsPreviewMedia({
  alt,
  className,
  posterUrl,
  sizes,
  videoUrl,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasVideoError, setHasVideoError] = useState(false);
  const canPreview = Boolean(videoUrl) && !hasVideoError;

  const playPreview = async () => {
    if (!videoRef.current || !canPreview) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return;

    try {
      await videoRef.current.play();
      setIsPlaying(true);
    } catch {
      setIsPlaying(false);
    }
  };

  const stopPreview = () => {
    if (!videoRef.current) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
    setIsPlaying(false);
  };

  return (
    <div
      role="img"
      aria-label={alt}
      className={`relative h-full w-full [&_img]:object-cover [&_video]:object-cover ${className ?? ''}`}
      onMouseEnter={playPreview}
      onMouseLeave={stopPreview}
    >
      <Image src={posterUrl} alt={alt} fill sizes={sizes} />
      {videoUrl && (
        <video
          className={`absolute inset-0 h-full w-full transition-opacity duration-[160ms] ease-[ease] ${
            isPlaying ? 'opacity-100' : 'opacity-0'
          }`}
          ref={videoRef}
          src={videoUrl}
          poster={posterUrl}
          muted
          loop
          playsInline
          preload="none"
          aria-hidden="true"
          onError={() => setHasVideoError(true)}
        />
      )}
      {canPreview && !isPlaying && (
        <span
          className="absolute bottom-2.5 right-2.5 grid h-7 w-7 place-items-center rounded-full bg-black/[0.68] pl-0.5 text-[11px] text-white"
          aria-hidden="true"
        >
          ▶
        </span>
      )}
    </div>
  );
}
